/**
 * QueryLogsTab Component
 *
 * Displays database query performance logs in a DataTable with filters and live updates
 */

import { useState, useMemo, useCallback } from 'react';
import { IconWifi, IconFilter, IconEye } from '@tabler/icons-react';
import { Stack, Card, Group, Button, Select, MultiSelect, Badge, Text, ActionIcon, Switch } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { QueryLogsLiveModal } from './LiveUpdatesModal';
import type { QueryLogsTabProps, QueryLogRow } from '../types';

// Helper: Format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Helper: Format duration
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

export function QueryLogsTab({ logs, onRefresh, loading }: QueryLogsTabProps) {
  const [liveModalOpened, setLiveModalOpened] = useState(false);
  const [filterQueryType, setFilterQueryType] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [showSlowOnly, setShowSlowOnly] = useState(false);
  const [filterServices, setFilterServices] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  // Get unique query types, entity types, and services for filters
  const { queryTypes, entityTypes, services } = useMemo(() => {
    const queryTypeSet = new Set<string>();
    const entityTypeSet = new Set<string>();
    const serviceSet = new Set<string>();

    logs.forEach((log) => {
      if (log.queryType) queryTypeSet.add(log.queryType);
      if (log.entityType) entityTypeSet.add(log.entityType);
      if (log.serviceName) serviceSet.add(log.serviceName);
    });

    return {
      queryTypes: Array.from(queryTypeSet).sort(),
      entityTypes: Array.from(entityTypeSet).sort(),
      services: Array.from(serviceSet).sort(),
    };
  }, [logs]);

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Query type filter
      if (filterQueryType && log.queryType !== filterQueryType) return false;

      // Entity type filter
      if (filterEntityType && log.entityType !== filterEntityType) return false;

      // Service filter (multi-select)
      if (filterServices.length > 0 && !filterServices.includes(log.serviceName || '')) return false;

      // Slow queries only
      if (showSlowOnly && !log.isSlowQuery) return false;

      return true;
    });
  }, [logs, filterQueryType, filterEntityType, filterServices, showSlowOnly]);

  // Convert to row format
  const rows: QueryLogRow[] = useMemo(() => {
    return filteredLogs.map((log) => ({
      ...log,
      id: log.id,
    }));
  }, [filteredLogs]);

  // Handle view details
  const handleViewDetails = useCallback((logId: number) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  }, [expandedLogId]);

  // Define columns
  const columns: DataTableColumn<QueryLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        render: (value) => (
          <Text size="xs" c="dimmed">
            {formatTimestamp(value as string)}
          </Text>
        ),
      },
      {
        key: 'queryType',
        label: 'Query Type',
        render: (value) => (
          <Badge size="sm" variant="light" color="blue">
            {value as string}
          </Badge>
        ),
      },
      {
        key: 'entityType',
        label: 'Entity',
        render: (value) => (
          <Text size="sm">{(value as string) || '-'}</Text>
        ),
      },
      {
        key: 'durationMs',
        label: 'Duration',
        sortable: true,
        render: (value) => (
          <Text size="xs" c={(value as number) > 1000 ? 'red' : 'dimmed'}>
            {formatDuration(value as number)}
          </Text>
        ),
      },
      {
        key: 'rowCount',
        label: 'Rows',
        sortable: true,
        render: (value) => (
          <Text size="xs">{(value as number).toLocaleString()}</Text>
        ),
      },
      {
        key: 'isSlowQuery',
        label: 'Slow',
        render: (value) =>
          value ? (
            <Badge size="sm" color="orange">
              Slow
            </Badge>
          ) : null,
      },
      {
        key: 'serviceName',
        label: 'Service',
        render: (value) => (
          <Text size="xs">{(value as string) || '-'}</Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <ActionIcon
            variant="light"
            size="sm"
            color="blue"
            onClick={() => handleViewDetails(row.id)}
            aria-label="View query details"
          >
            <IconEye size={14} />
          </ActionIcon>
        ),
      },
    ],
    [handleViewDetails]
  );

  return (
    <Stack gap="md">
      {/* Actions and Filters */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Group gap="md">
            <Button
              leftSection={<IconWifi size={16} />}
              onClick={() => setLiveModalOpened(true)}
              variant="gradient"
              gradientVariant="primary"
            >
              Live Updates
            </Button>
            <Button
              variant="light"
              onClick={onRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>

          <Group gap="md">
            <Select
              placeholder="Query Type"
              value={filterQueryType}
              onChange={(value) => setFilterQueryType(value || '')}
              data={[
                { value: '', label: 'All Query Types' },
                ...queryTypes.map((qt) => ({ value: qt, label: qt })),
              ]}
              leftSection={<IconFilter size={16} />}
              style={{ minWidth: 200 }}
            />
            <Select
              placeholder="Entity Type"
              value={filterEntityType}
              onChange={(value) => setFilterEntityType(value || '')}
              data={[
                { value: '', label: 'All Entities' },
                ...entityTypes.map((et) => ({ value: et, label: et })),
              ]}
              leftSection={<IconFilter size={16} />}
              style={{ minWidth: 200 }}
            />
            <MultiSelect
              placeholder="Filter by service"
              value={filterServices}
              onChange={setFilterServices}
              data={services.map((s) => ({ value: s, label: s }))}
              leftSection={<IconFilter size={16} />}
              clearable
              style={{ minWidth: 280 }}
            />
            <Switch
              label="Slow queries only"
              checked={showSlowOnly}
              onChange={(e) => setShowSlowOnly(e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Card>

      {/* DataTable */}
      <Card padding="lg">
        <DataTable<QueryLogRow>
          data={rows}
          columns={columns}
          pagination={{
            page,
            pageSize,
            total: rows.length,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          gradientVariant="primary"
          highlightOnHover
          striped
          stickyHeader
          withRowAnimations
          emptyMessage="No query logs found. Try adjusting your filters."
          enableInternalSort
          defaultSortKey="timestamp"
          defaultSortDirection="desc"
        />
      </Card>

      <Text size="sm" c="dimmed">
        Showing {rows.length} of {logs.length} queries
      </Text>

      {/* Live Updates Modal */}
      <QueryLogsLiveModal
        opened={liveModalOpened}
        onClose={() => setLiveModalOpened(false)}
        serviceNames={filterServices.length > 0 ? filterServices : undefined}
      />
    </Stack>
  );
}
